import { DrawInteractionTool } from './tool-base';
import Feature from 'ol/Feature';
import { transform as olTransform } from 'ol/proj';
import { Type as OlGeometryType } from 'ol/geom/Geometry';
import { LineString, Point } from 'ol/geom';
import { getGeomLength, getPointsDistance } from '@openlayers/ol-utils';

export class DistanceTool extends DrawInteractionTool {
    geometryType: OlGeometryType = 'LineString';

    snappedFeatures: Feature[] = [];

    constructor(...args: ConstructorParameters<typeof DrawInteractionTool>) {
        super(...args);

        // when map changes we need to redraw segment labels
        setTimeout(() => {
            this.mapWrapper.featureLayers.logpoints?.layerSource.on('change', this.onMapChanged);
            this.mapWrapper.featureLayers.pipes?.layerSource.on('change', this.onMapChanged);
            this.mapWrapper.featureLayers.points?.layerSource.on('change', this.onMapChanged);
        }, 1);
    }

    onMapChanged = _.debounce(() => {
        if (this.drawLayer.getSource().isEmpty()) return;
        this.drawLayer
            .getSource()
            .getFeatures()
            .forEach(f => this.drawLabels(f));
    }, 300);

    onDrawStart(e) {
        // : OlDrawEvent
        const currFeature: Feature = e.feature;
        this.geometryListener = this.bindEvents(currFeature);
        this.snappedFeatures = [];
        super.onDrawStart(e);
    }

    getLabelText(line): string {
        const coordinates = line.getCoordinates();
        const n = coordinates.length;
        const sourceProj = this.mapWrapper.getProjection();
        const length = getGeomLength(line, this.mapWrapper.getProjection());

        let output = this.formatLength(length);

        // radians, gradians
        if (n >= 3) {
            const pos3 = olTransform(coordinates[n - 3], sourceProj, kuura.getProjectProj4Code());
            const pos2 = olTransform(coordinates[n - 2], sourceProj, kuura.getProjectProj4Code());
            const pos1 = olTransform(coordinates[n - 1], sourceProj, kuura.getProjectProj4Code());
            const a = getPointsDistance(pos3, pos2);
            const c = getPointsDistance(pos2, pos1);
            const b = getPointsDistance(pos1, pos3);

            if (a == 0 || b == 0 || c == 0) return output;

            const angRad = Math.acos((Math.pow(a, 2) + Math.pow(c, 2) - Math.pow(b, 2)) / (2 * a * c));

            const angDeg = (angRad * 180) / Math.PI;
            const angGrad = (angRad * 200) / Math.PI;

            // surveyors like the word gon instead of grad
            output += ` | deg ${angDeg.toFixed(2)} | gon ${angGrad.toFixed(2)}`;
        }

        return output;
    }

    restoreFromCoords(coords: number[][][], sourceUuid?: string): Feature[] {
        if (!Array.isArray(coords)) return;

        const source = this.drawLayer.getSource();
        return coords.map(measurement => {
            const feature = new Feature({
                geometry: new LineString(measurement),
            });
            feature.set('finished', true);
            const geom = feature.getGeometry();
            source.addFeature(feature);
            this.bindEvents(feature);
            feature.getGeometry().dispatchEvent('change');
            feature.set('sourceUuid', sourceUuid);
            return feature;
        });
    }

    lastClickCoords = [];
    bindEvents = feature => {
        const evt = feature.getGeometry().on('change', e => {
            const geom = feature.getGeometry();
            const coords = geom.getCoordinates();

            if (this.lastClickCoords.length !== coords.length) {
                this.onDrawProgressCallback(feature);
            }
            this.lastClickCoords = coords;

            this.drawLabels(feature);
        });

        feature.set('type', 'distance-measurement');

        return evt;
    };

    drawLabels = _.throttle((feature: Feature) => {
        if (!feature) return;

        this.clearFeatureLabels(feature);

        const geometry = feature.getGeometry() as LineString;
        const coordinates = geometry.getCoordinates();
        if (!coordinates || coordinates.length < 2) return;

        // total label
        if (coordinates.length >= 2) {
            const totalText = this.getLabelText(geometry);
            const totalCoordinate = geometry.getLastCoordinate();
            if (feature.get('finished')) {
                this.removeInProgressLabel();
                this.createLabelFeature(feature, totalText, totalCoordinate, false);
            } else {
                this.updateInProgressLabel(totalText, totalCoordinate, false);
            }
        }

        // segment labels
        if (this.showSegmentLabels && coordinates.length > 2) {
            const sourceProj = this.mapWrapper.getProjection();

            for (let i = 1; i < coordinates.length; i++) {
                const prevCoord = coordinates[i - 1];
                const currentCoord = coordinates[i];

                // measurement distance
                const segmentLength = getPointsDistance(
                    olTransform(prevCoord, sourceProj, kuura.getProjectProj4Code()),
                    olTransform(currentCoord, sourceProj, kuura.getProjectProj4Code())
                );
                let segmentText = this.formatLength(segmentLength);

                if (segmentLength == 0) continue;

                // snapped points slope
                const prevFeature = this.getFeatureAtCoordinate(prevCoord);
                const currentFeature = this.getFeatureAtCoordinate(currentCoord);

                if (prevFeature && currentFeature) {
                    const prevZ = prevFeature.get('data')?.z ?? prevFeature.get('data')?.elevation;
                    const currentZ = currentFeature.get('data')?.z ?? currentFeature.get('data')?.elevation;

                    if (prevZ != null && currentZ != null) {
                        const zDiff = currentZ - prevZ;

                        const directDistance = getPointsDistance(
                            olTransform((prevFeature.getGeometry() as Point).getCoordinates(), sourceProj, kuura.getProjectProj4Code()),
                            olTransform((currentFeature.getGeometry() as Point).getCoordinates(), sourceProj, kuura.getProjectProj4Code())
                        );

                        if (directDistance > 0) {
                            const distance3d = Math.sqrt(Math.pow(directDistance, 2) + Math.pow(zDiff, 2));
                            const slopePercent = (zDiff / directDistance) * 100;
                            const slopeAngle = Math.atan(zDiff / directDistance) * (180 / Math.PI);

                            segmentText += `\n${__('js_map_measuring_tools.3d_distance_short_label')}: ${this.formatLength(distance3d)}`;
                            segmentText += `\n${__('js_map_measuring_tools.slope')}: ${slopePercent.toFixed(2)}%`;
                            segmentText += `\n${__('js_map_measuring_tools.z_diff')}: ${zDiff.toFixed(2)}m`;
                        }
                    }
                }

                this.createLabelFeature(feature, segmentText, [(prevCoord[0] + currentCoord[0]) / 2, (prevCoord[1] + currentCoord[1]) / 2], true);
            }
        }
    }, 32);
}
